/**
 * Created by Abner Sui on 01/30/2019.
 * Description:
 *
 * ------ maintenance history ------
 */

import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of, forkJoin } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';

import { AppState, smartEffect } from '..';
import { BlastEmailViewBlastEmailListActionTypes, BlastEmailViewActionTypes, BlastEmailRefinebyActionTypes, BlastEmailViewBlastEmailListDelete, BlastEmailRefinebyLoad, BlastEmailViewBlastEmailListReload, BlastEmailViewBlastEmailListLoadMore, BlastEmailViewGetBlastEmailBody } from '../actions/blastemail-view.actions';

import { DataList } from '../../tamalelibs/models/data-list.model';
import { BlastEmail } from '../../tamalelibs/models/blastemail.model';
import { BlastEmailService } from '../../tamalelibs/services/blastemail.service';
import { StoreQuerierService } from '../../services/store-querier.service';

@Injectable()
export class BlastEmailViewEffects {
    constructor(
        private _actions$: Actions,
        private _store: Store<AppState>,
        private _blastEmailService: BlastEmailService
    ) { }

    deleteBlastEmailEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(BlastEmailViewBlastEmailListActionTypes.DELETE_BLASTEMAIL),
        switchMap((action: BlastEmailViewBlastEmailListDelete) => {
            const payload = action.payload;
            return this._blastEmailService.deleteBlastEmailById(payload.id).pipe(
                map(response => {
                    return smartEffect(action, {
                        type: BlastEmailViewBlastEmailListActionTypes.DELETE_BLASTEMAIL_SUCESS,
                        payload: payload.componentId
                    });
                }),
                catchError(err => {
                    return of(smartEffect(action, {
                        type: BlastEmailViewBlastEmailListActionTypes.DELETE_BLASTEMAIL_FAILED,
                        payload: payload.componentId + '-' + err.message
                    }));
                }),
            );
        })
    ));

    loadBlastemailRefinebyEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(BlastEmailRefinebyActionTypes.LOAD),
        switchMap((action: BlastEmailRefinebyLoad) => {
            const payload = action.payload;
            const category: Array<string> = payload.category;
            return this._blastEmailService.getRefinebyData(payload.searchText, category, payload.categoryFilter).pipe(
                map(response => {
                    const data: Map<string, Array<any>> = new Map();
                    category.forEach(id => {
                        data.set(id, response[id]);
                    });
                    return smartEffect(action, {
                        type: BlastEmailRefinebyActionTypes.LOAD_SUCCESS,
                        payload: data,
                    });
                }),
                catchError(err => {
                    return of(smartEffect(action, {
                        type: BlastEmailRefinebyActionTypes.LOAD_FAILED,
                        payload: err.message
                    }));
                })
            );
        })
    ));

    reloadBlastEmailListEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(BlastEmailViewBlastEmailListActionTypes.RELOAD),
        switchMap((action: BlastEmailViewBlastEmailListReload) => {
            const payload = action.payload;
            const pageIndex = payload.pageIndex;
            const pageSize = payload.pageSize;
            const sortBy = payload.sortBy;

            const sortOrder = payload.sortOrder || 'desc';

            return this._blastEmailService.queryByParams(pageIndex, pageSize, sortBy, sortOrder, payload.searchText, payload.categoryFilter).pipe(
                map(response => {
                    let matchedAttachmentSet: Set<string> = new Set();
                    if (payload.searchText) {
                        matchedAttachmentSet = this._parseHitAttachmentIdListInBlastEmailList(response);
                    }

                    const blastEmailList = this._parseBlastEmailList(response);
                    return smartEffect(action, {
                        type: BlastEmailViewBlastEmailListActionTypes.RELOAD_SUCCESS,
                        payload: {
                            blastEmails: blastEmailList,
                            matchedAttachmentSet: matchedAttachmentSet
                        }
                    });
                }),
                catchError(err => {
                    return of(smartEffect(action, {
                        type: BlastEmailViewBlastEmailListActionTypes.RELOAD_FAILED,
                        payload: err.message
                    }));
                })
            );
        })
    ));

    loadMoreBlastEmailListEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(BlastEmailViewBlastEmailListActionTypes.LOAD_MORE),
        switchMap((action: BlastEmailViewBlastEmailListLoadMore) => {
            const payload = action.payload;
            const pageIndex = payload.pageIndex;
            const pageSize = payload.pageSize;
            const sortBy = payload.sortBy;

            const sortOrder = payload.sortOrder || 'desc';

            const observables$ = [];
            observables$.push(this._blastEmailService.queryByParams(pageIndex - 1, pageSize, sortBy, sortOrder, payload.searchText, payload.categoryFilter));
            observables$.push(this._blastEmailService.queryByParams(pageIndex, pageSize, sortBy, sortOrder, payload.searchText, payload.categoryFilter));

            return forkJoin(observables$).pipe(
                map((responses: any) => {
                    // combine two response
                    const response = {
                        count: responses[1].count,
                        next: responses[1].next
                    };
                    response['blastEmailList'] = responses[0]['blastEmailList'].concat(responses[1]['blastEmailList']);
                    return response;
                }),
                map(response => {
                    let matchedAttachmentSet: Set<string> = new Set();
                    if (payload.searchText) {
                        matchedAttachmentSet = this._parseHitAttachmentIdListInBlastEmailList(response);
                    }
                    const blastEmailList = this._parseBlastEmailList(response);
                    return smartEffect(action, {
                        type: BlastEmailViewBlastEmailListActionTypes.LOAD_MORE_SUCCESS,
                        payload: {
                            pageIndex: pageIndex,
                            pageSize: pageSize,
                            blastEmails: blastEmailList,
                            matchedAttachmentSet: matchedAttachmentSet
                        }
                    });
                })
            );
        })
    ));

    loadBlastEmailNoteBodiesEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(BlastEmailViewActionTypes.GET_BLASTEMAIL_BODY),
        switchMap((action: BlastEmailViewGetBlastEmailBody) => {
            const payload: BlastEmail = action.payload;
            if (!payload.body) {
                return this._blastEmailService.getBlastEmailBodyById(payload.id).pipe(
                    map(response => {
                        payload.body = response;
                        return smartEffect(action, {
                            type: BlastEmailViewActionTypes.GET_BLASTEMAIL_BODY_SUCCESS,
                            payload: payload
                        });
                    })
                );
            }
        })
    ));

    private _parseBlastEmailList(response) {
        const blastEmailList: DataList<BlastEmail> = new DataList<BlastEmail>();

        blastEmailList.totalCount = response.count;
        blastEmailList.hasNext = !!response.next;
        response['blastEmailList'].forEach(element => {
            const blastEmail = BlastEmail.parse(element);
            blastEmailList.push(blastEmail);
        });

        return blastEmailList;
    }

    private _parseHitAttachmentIdListInBlastEmailList(response): Set<string> {
        const idSet: Set<string> = new Set<string>();
        response['blastEmailList'].forEach(element => {
            element['hits'].forEach(id => {
                idSet.add(id);
            });
        });

        return idSet;
    }

}
